import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="image-upload"
export default class extends Controller {
    static targets = [
        "input", "fileInput", "dropZone",
        "idleState", "uploadState", "optimizingState", "previewState",
        "progressBar", "progressText", "previewImage", "successBadge"
    ]

    static values = {
        uploadUrl: String
    }

    connect() {
        this.csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute('content')
    }

    trigger(e) {
        if (e) e.stopPropagation()
        this.fileInputTarget.click()
    }

    dragOver(e) {
        e.preventDefault()
        this.dropZoneTarget.classList.add("border-indigo-500", "bg-indigo-50")
    }

    dragLeave(e) {
        e.preventDefault()
        this.dropZoneTarget.classList.remove("border-indigo-500", "bg-indigo-50")
    }

    drop(e) {
        e.preventDefault()
        this.dropZoneTarget.classList.remove("border-indigo-500", "bg-indigo-50")

        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            this.handleFile(e.dataTransfer.files[0])
        }
    }

    handleFileSelect(e) {
        if (e.target.files && e.target.files[0]) {
            this.handleFile(e.target.files[0])
        }
    }

    handleFile(file) {
        // Validate file type
        const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
        if (!validTypes.includes(file.type)) {
            alert("Choisissez une image valide (JPG, PNG, GIF, WEBP)")
            return
        }

        // Validate size (10MB max)
        if (file.size > 10 * 1024 * 1024) {
            alert("Image trop lourde (10 Mo maximum)")
            return
        }

        this.uploadFile(file)
    }

    async uploadFile(file) {
        // 1. UI Transition to Uploading
        this.showState('upload')

        try {
            // 2. Get Direct Upload URL from our Backend
            const response = await fetch(this.uploadUrlValue, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': this.csrfToken
                }
            })

            const data = await response.json()

            if (!data.success) throw new Error(data.error || "Failed to get upload URL")

            const uploadUrl = data.upload_url

            // 3. Upload to Cloudflare
            const formData = new FormData()
            formData.append('file', file)

            const xhr = new XMLHttpRequest()
            xhr.open('POST', uploadUrl, true)

            // Progress handling
            xhr.upload.onprogress = (e) => {
                if (e.lengthComputable) {
                    const percentComplete = Math.round((e.loaded / e.total) * 100)
                    this.updateProgress(percentComplete)
                }
            }

            xhr.onload = async () => {
                if (xhr.status === 200) {
                    const cfResponse = JSON.parse(xhr.responseText)
                    if (cfResponse.success) {
                        // Success! Get the public variant URL
                        // Usually variants[0] is the public/default one or we prefer 'public' named variant
                        const result = cfResponse.result
                        let imageUrl = result.variants[0] // Default fallback

                        // Try to find a variant ending in 'public' or take the first one
                        const publicVariant = result.variants.find(v => v.endsWith('public'))
                        if (publicVariant) imageUrl = publicVariant

                        // 4. Simulate Optimization (The "Magic" part)
                        this.showState('optimizing')

                        // Artificial delay to show the "Compressing" animation (1.5s)
                        await new Promise(r => setTimeout(r, 1500))

                        // 5. Finalize
                        this.completeUpload(imageUrl)
                    } else {
                        this.handleError("Envoi refusé par Cloudflare")
                    }
                } else {
                    this.handleError("L'envoi a échoué")
                }
            }

            xhr.onerror = () => this.handleError("Erreur réseau")
            xhr.send(formData)

        } catch (error) {
            console.error(error)
            this.handleError(error.message)
        }
    }

    updateProgress(percent) {
        this.progressBarTarget.style.width = `${percent}%`
        this.progressTextTarget.textContent = `${percent}%`
    }

    showState(state) {
        // Hide all states first
        this.idleStateTarget.classList.add('hidden')
        this.uploadStateTarget.classList.add('hidden')
        this.optimizingStateTarget.classList.add('hidden')
        this.previewStateTarget.classList.add('hidden')

        // Show requested state
        switch (state) {
            case 'idle':
                this.idleStateTarget.classList.remove('hidden')
                break
            case 'upload':
                this.uploadStateTarget.classList.remove('hidden')
                this.progressBarTarget.style.width = '0%'
                this.progressTextTarget.textContent = '0%'
                break
            case 'optimizing':
                this.optimizingStateTarget.classList.remove('hidden')
                break
            case 'preview':
                this.previewStateTarget.classList.remove('hidden')
                break
        }
    }

    completeUpload(url) {
        this.inputTarget.value = url
        this.inputTarget.dispatchEvent(new Event('change', { bubbles: true }))
        this.previewImageTarget.src = url

        // Flash the success badge
        this.successBadgeTarget.classList.add('animate-pulse')
        setTimeout(() => this.successBadgeTarget.classList.remove('animate-pulse'), 2000)

        this.showState('preview')

        // Dispatch custom event for auto-save and live preview update
        this.element.dispatchEvent(new CustomEvent('image:uploaded', {
            detail: { url: url },
            bubbles: true
        }))
    }

    handleError(msg) {
        alert(msg)
        this.showState('idle') // Reset
    }

    remove(e) {
        e.stopPropagation() // Prevent triggering the file input
        if (confirm("Retirer cette image ?")) {
            this.inputTarget.value = ""
            this.inputTarget.dispatchEvent(new Event('change', { bubbles: true }))
            this.previewImageTarget.src = ""
            this.showState('idle')
        }
    }
}
